import {
  withCompInfo,
  createComponentMapperModel,
  withStateRefsValues,
} from '@wix/editor-elements-integrations';
import type {
  CompDefinition,
  CompInfo,
} from '@wix/editor-elements-types/thunderbolt';
import {
  getScaledFont,
  getHeightInPixels,
  withSiteDirection,
  withFallbackValues,
  convertPhysicalInputAlignmentToDirection,
} from '@wix/editor-elements-common-utils';
import type { BaseDataItem } from '@wix/editor-elements-definitions';
import type { AbsoluteLayout } from '@wix/document-services-types';
import type {
  ICheckboxMapperProps,
  CheckboxDefinition,
  ICheckboxStateValues,
  CheckboxCSSVars,
  ICheckboxCarmiProps,
  CheckboxData,
  CheckboxProperties,
} from '../Checkbox.types';

export const props = withCompInfo<
  ICheckboxMapperProps,
  CheckboxDefinition,
  ICheckboxCarmiProps
>()(
  [
    'fullNameCompType',
    'language',
    'compId',
    'mainPageId',
    'trackClicksAnalytics',
  ],
  (compProps, carmiData) => {
    return {
      ...carmiData,
      ...compProps,
    };
  },
);

export const generateCheckboxCSSVars = <
  Data extends Pick<CheckboxData, 'label'> & BaseDataItem,
  Properties extends Pick<
    CheckboxProperties,
    'alignment' | 'buttonSize' | 'spacing' | 'inputMobileFontSize'
  > &
    BaseDataItem,
>(
  compInfo: Pick<
    CompInfo<CompDefinition<any, Data, Properties, any>>,
    | 'compLayout'
    | 'styleProperties'
    | 'compProps'
    | 'compData'
    | 'hasResponsiveLayout'
    | 'siteFonts'
    | 'isMobileView'
    | 'siteFontsSpxResolved'
  >,
  calculatedDependencies: {
    size: number;
    labelMarginStart: string;
  },
): CheckboxCSSVars => {
  const { compProps, hasResponsiveLayout } = compInfo;
  const { inputMobileFontSize } = compProps;
  const { size, labelMarginStart } = calculatedDependencies;

  const checkboxSize = `${size}px`;
  const fnt = getScaledFont(compInfo, 'fnt', inputMobileFontSize);

  return {
    '--checkboxSize': checkboxSize,
    '--fnt': fnt,
    '--checkmarkMargin_start': `-${size}px`,
    '--labelMargin_start': labelMarginStart,
    ...(!hasResponsiveLayout && {
      height: 'auto',
    }),
  };
};

type Overrides = {
  compLayout: AbsoluteLayout;
};

export const css = withCompInfo<
  CheckboxCSSVars,
  CheckboxDefinition,
  never,
  Overrides
>()(
  [
    'compProps',
    'compData',
    'compLayout',
    'compSingleLayout',
    'hasResponsiveLayout',
    'structure',
    'styleProperties',
    'siteFonts',
    'isMobileView',
    'isOneDocMigrated',
  ],
  compInfo => {
    const {
      hasResponsiveLayout,
      compProps,
      compLayout,
      compSingleLayout,
      compData,
      styleProperties,
      isOneDocMigrated,
    } = compInfo;
    const { buttonSize, spacing } = compProps;
    const { label, direction = 'inherit' } = compData;
    const { align = 'start' } = styleProperties;
    let size = buttonSize;
    if (!hasResponsiveLayout && !label) {
      size = isOneDocMigrated
        ? getHeightInPixels(compSingleLayout)!
        : compLayout.height;
    }
    return {
      ...generateCheckboxCSSVars(compInfo, {
        size,
        labelMarginStart: label ? `${spacing + 1}px` : 'auto',
      }),
      '--checkbox-direction': withSiteDirection(direction),
      '--align': align,
    };
  },
  [
    withFallbackValues([
      {
        sourceNamespace: 'compProps',
        targetNamespace: 'compData',
        fields: [{ source: 'alignment', target: 'direction' }],
        enhancer: convertPhysicalInputAlignmentToDirection,
      },
    ]),
  ],
);

const stateRefs = withStateRefsValues<keyof ICheckboxStateValues>(['reportBi']);

export default createComponentMapperModel({ props, css, stateRefs });
